import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface SectionHeadingProps {
  /** Thai title — always first */
  th: ReactNode
  /** Short English label (1–3 words), set uppercase */
  en?: string
  description?: ReactNode
  /** Page title (h1) or section title (h2) */
  level?: 1 | 2
  align?: 'center' | 'start'
  className?: string
}

/**
 * Design-system SectionHeading: centred Thai title with a small tracked
 * English label beneath. Thai first, English second — never swapped.
 */
export function SectionHeading({
  th,
  en,
  description,
  level = 2,
  align = 'center',
  className,
}: SectionHeadingProps) {
  const HeadingTag = level === 1 ? 'h1' : 'h2'
  return (
    <header className={cn(align === 'center' ? 'text-center' : 'text-left', className)}>
      <HeadingTag
        className={cn(
          'm-0 font-thai-display font-bold text-ink [text-shadow:0_0_28px_rgba(224,214,154,0.18)]',
          level === 1
            ? 'text-[32px] leading-[46px] sm:text-[40px] sm:leading-[54px]'
            : 'ds-section-th'
        )}
      >
        {th}
      </HeadingTag>
      {en ? <span className="ds-label mt-2 block text-gold-solid">{en}</span> : null}
      {description ? (
        <p
          className={cn(
            'mt-3 max-w-[60ch] text-[14px] leading-[22px] text-ink-muted',
            align === 'center' && 'mx-auto'
          )}
        >
          {description}
        </p>
      ) : null}
    </header>
  )
}
